/**
 * Ручки auth, которыми пользуется трекер. Сервис общий с CRM, поэтому и
 * контракт тот же — включая две его особенности:
 *
 * — логин принимает поле `username`, хотя в нём почта;
 * — refresh читает токен из заголовка `Refresh-Token`, тело не смотрит.
 */
import { http } from '@/api/http'

import type { LoginResponse, Person, User } from './types'

/** Сколько сотрудников тянем разом: справочник VINPROP умещается в одну страницу */
const USERS_PAGE = 500

interface PersonResponse {
  id?: string
  name?: string
  email?: string
  firstName?: string
  lastName?: string
  roles?: string[]
}

/**
 * Кого в трекер не зовут никогда (DEV-1591).
 *
 * Агенты и партнёры — внешние люди: у них свои кабинеты, а задач в проектах
 * компании они не ведут. Сервисные учётки — это токены сервисов, человека за
 * ними нет вовсе. Раньше все они лежали в подсказках поиска участников и
 * мешали найти нужного.
 */
const OUTSIDE_ROLES = new Set(['AGENT', 'PARTNER'])

const isOutsider = (roles: string[]): boolean =>
  roles.length > 0 &&
  roles.every((role) => OUTSIDE_ROLES.has(role) || role.startsWith('SERVICE_'))

/**
 * Имя человека. Поле `name` в auth помечено устаревшим в пользу пары
 * `first_name`/`last_name`, но заполнено не у всех — берём что есть, иначе в
 * списке участников окажется строка без имени.
 */
const toPerson = (person: PersonResponse): Person => ({
  id: person.id ?? '',
  email: person.email ?? '',
  name: [person.firstName, person.lastName].filter(Boolean).join(' ') || person.name || '',
  roles: person.roles ?? [],
})

export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    const { data } = await http.post<LoginResponse>('/api/v1/auth/login', {
      username: email,
      password,
    })

    return data
  },

  async logout(): Promise<void> {
    await http.post('/api/v1/auth/logout')
  },

  async me(): Promise<User> {
    const { data } = await http.get<User>('/api/v1/auth/me')

    return data
  },

  /**
   * Люди по идентификаторам — почты участников проекта. Трекер про почты не
   * знает: в `ProjectMember` у него только идентификатор, роль и имя.
   */
  async usersByIds(ids: string[]): Promise<Person[]> {
    if (!ids.length) return []

    const { data } = await http.post<{ users?: PersonResponse[] }>(
      '/api/v1/auth/users/by-ids',
      { ids },
    )

    return (data.users ?? []).map(toPerson)
  },

  /**
   * Справочник сотрудников — чтобы добавить человека в проект. Поиска по
   * подстроке у ручки нет, только страницы, поэтому берём одну большую и ищем
   * на месте. Право `users:read` есть не у всех: экран спрашивает его до
   * запроса и без него не показывает поле вовсе.
   */
  async listUsers(perPage = USERS_PAGE): Promise<Person[]> {
    const { data } = await http.get<{ users?: PersonResponse[] }>('/api/v1/auth/users', {
      params: { 'pagination.page': 1, 'pagination.perPage': perPage },
    })

    // Отбор здесь, а не на экране: круг людей у трекера один на все его поля
    // поиска, и повторять правило в каждом — верный способ их развести.
    return (data.users ?? []).map(toPerson).filter((person) => !isOutsider(person.roles))
  },
}
